import { Divider, Stack, useTheme } from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPortfolioReportingSummaryQuery } from "web_myplann/Api/Portfolio/getPortfolioReportingSummaryQuery";
import { usePortfolioListQuery } from "web_myplann/Api/Portfolio/usePortfolioListQuery";
import { usePropertyListQuery } from "web_myplann/Api/Portfolio/usePropertyListQuery";
import {
	ClientPortfolioDomainEnum,
	ClientPropertyTypeEnum,
	PortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { GroupedListLabel } from "web_myplann/Portfolio/Components/GroupedListLabel";
import { GroupedPortfolioListItem } from "web_myplann/Portfolio/Components/GroupedPortfolioListItem";
import { PortfolioListItemTitle } from "web_myplann/Portfolio/Components/PortfolioSections/PortfolioListItemTitle";
import { PropertyListItemTitle } from "web_myplann/Portfolio/Components/PropertySections/PropertyListItemTitle";
import {
	type SortKey,
	SortTypes,
} from "web_myplann/Portfolio/Components/SortDrawer";
import { StickyGroupedListLabel } from "web_myplann/Portfolio/Components/StickyGroupedListLabel";
import {
	type GroupedPortfolioList,
	useGroupPortfolio,
} from "web_myplann/Portfolio/Hooks/useGroupPortfolio";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";
import { useIsDarkMode } from "web_myplann/Shared/Renderer/useIsDarkMode";
import { SabSkeleton } from "web_myplann/Shared/Skeletons";
import { BAR_MYPLANN_LIGHT } from "web_myplann/Utils/constants";

export const specialPortfolioListDomains = [
	ClientPortfolioDomainEnum.PensionProducts,
	ClientPortfolioDomainEnum.BuildingSavings,
	ClientPortfolioDomainEnum.Investments,
	ClientPortfolioDomainEnum.Commodities,
	ClientPortfolioDomainEnum.BankingProducts,
	ClientPortfolioDomainEnum.Cryptocurrencies,
	ClientPortfolioDomainEnum.Loans,
	ClientPortfolioDomainEnum.BusinessLoans,
	ClientPortfolioDomainEnum.Leasing,
];

export type PortfolioSubType =
	| ClientPortfolioDomainEnum
	| ClientPropertyTypeEnum;

type Props = {
	activeSort: SortKey;
	setIsLoading: (isFetching: boolean) => void;
};

export const PortfolioListPage: React.FunctionComponent<Props> = ({
	activeSort,
	setIsLoading,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const isDarkMode = useIsDarkMode();

	const [stickyTitle, setStickyTitle] = useState<string>("");
	const [specialPortfolioListIds, setSpecialPortfolioListIds] = useState<
		number[]
	>([]);

	const { data: portfolioList, isLoading: isPortfolioLoading } =
		usePortfolioListQuery();
	const { data: propertyList, isLoading: isPropertyLoading } =
		usePropertyListQuery();
	const { data: portfolioReportingSummary } = getPortfolioReportingSummaryQuery(
		specialPortfolioListIds,
	);

	useEffect(() => {
		if (portfolioList?.data) {
			const newIds =
				portfolioList.data
					.filter(
						(p) =>
							p.domain !== undefined &&
							p.domain !== null &&
							specialPortfolioListDomains.includes(p.domain),
					)
					.map((p) => p.id) ?? [];

			setSpecialPortfolioListIds(newIds);
		}
	}, [portfolioList?.data]);

	const groupedPortfolioList = useGroupPortfolio(portfolioList?.data ?? []);
	// ToDo: Uncomment when property grouping is needed
	// const groupedPropertyList = useGroupProperty(propertyList?.data ?? []);

	const groupedPortfolioListWithoutServicesAndUncategorized: GroupedPortfolioList =
		useMemo(() => {
			const {
				Services: _Services,
				Uncategorized: _Uncategorized,
				...rest
			} = groupedPortfolioList;
			return rest;
		}, [groupedPortfolioList]);

	const groupedPortfolioListServicesAndUncategorized: GroupedPortfolioList =
		useMemo(
			() => ({
				Services: groupedPortfolioList.Services ?? [],
				Uncategorized: groupedPortfolioList.Uncategorized ?? [],
			}),
			[groupedPortfolioList],
		);

	useEffect(() => {
		const firstCategory = Object.keys(groupedPortfolioList)[0];

		if (firstCategory) {
			const title = t(
				Resources.Portfolio.List.PortfolioDomainType[
					firstCategory as ClientPortfolioDomainEnum
				],
			);

			setStickyTitle(title);
		} else if (propertyList?.data && propertyList.data.length > 0) {
			setStickyTitle(t(Resources.Portfolio.List.Property));
		}
	}, [groupedPortfolioList, t, propertyList?.data]);

	useEffect(() => {
		setIsLoading(isPortfolioLoading || isPropertyLoading);
	}, [isPortfolioLoading, isPropertyLoading, setIsLoading]);

	const sortedPortfolioData = useMemo(() => {
		if (activeSort === SortTypes.producer) {
			return (
				portfolioList?.data.sort(
					(a, b) => a.producer?.localeCompare(b.producer ?? "") ?? 0,
				) ?? []
			);
		}

		if (activeSort === SortTypes.date) {
			return (
				portfolioList?.data.sort(
					(a, b) =>
						new Date(a.endAt ?? "").getTime() -
						new Date(b.endAt ?? "").getTime(),
				) ?? []
			);
		}

		return [];
	}, [portfolioList?.data, activeSort]);

	const sortedPropertyData = useMemo(() => {
		const propertyOrder = [
			ClientPropertyTypeEnum.AutoMoto,
			ClientPropertyTypeEnum.Flat,
			ClientPropertyTypeEnum.House,
			ClientPropertyTypeEnum.Cottage,
			ClientPropertyTypeEnum.Commodity,
			ClientPropertyTypeEnum.Land,
			ClientPropertyTypeEnum.Art,
		];

		return propertyList?.data.sort(
			(a, b) =>
				propertyOrder.indexOf(a.property ?? ClientPropertyTypeEnum.Commodity) -
				propertyOrder.indexOf(b.property ?? ClientPropertyTypeEnum.Commodity),
		);
	}, [propertyList?.data]);

	const navigateToPortfolioDetail = (id: number, type: PortfolioTypeEnum) => {
		navigate(Routing.Portfolio.PortfolioDetail.path(id.toString(), type));
	};

	const isDataEmpty =
		portfolioList?.data &&
		portfolioList?.data?.length === 0 &&
		propertyList?.data &&
		propertyList?.data?.length === 0 &&
		!isPortfolioLoading &&
		!isPropertyLoading;

	return (
		<Stack data-testid="portfolio-list-page">
			{isDataEmpty ? (
				<EmptyListSkeleton
					label={t(Resources.Portfolio.List.Skeleton.label)}
					description={t(Resources.Portfolio.List.Skeleton.description)}
				/>
			) : (
				<>
					{activeSort === SortTypes.default &&
						(stickyTitle ? (
							<StickyGroupedListLabel title={stickyTitle} />
						) : isPortfolioLoading || isPropertyLoading ? (
							<Stack
								height={40}
								p={"2px 8px"}
								sx={{
									background: isDarkMode
										? theme.palette.grey[50]
										: BAR_MYPLANN_LIGHT,
								}}
							>
								<SabSkeleton width="100%" height="100%" />
							</Stack>
						) : null)}

					{activeSort === SortTypes.default ? (
						Object.keys(
							groupedPortfolioListWithoutServicesAndUncategorized,
						).map((item, index) => (
							<GroupedPortfolioListItem
								key={item}
								item={item}
								list={groupedPortfolioListWithoutServicesAndUncategorized}
								navigateToPortfolioDetail={navigateToPortfolioDetail}
								type={PortfolioTypeEnum.Portfolio}
								setStickyTitle={setStickyTitle}
								hideGroupedListLabel={index === 0}
								summaryData={portfolioReportingSummary?.data}
							/>
						))
					) : (
						<Stack gap={1.6} mt={1.2}>
							{sortedPortfolioData?.map((r) => (
								<Stack key={r.id} gap={1.6}>
									<PortfolioListItemTitle
										data={r}
										summaryData={portfolioReportingSummary?.data}
										onClick={() =>
											navigateToPortfolioDetail(
												r.id,
												PortfolioTypeEnum.Portfolio,
											)
										}
									/>
									<Divider
										sx={{
											borderWidth: 0.75,
											borderColor: theme.palette.grey[100],
										}}
									/>
								</Stack>
							))}
						</Stack>
					)}

					{/* ToDo: Uncomment when property grouping is needed */}
					{/* {activeSort === SortTypes.default ? (
				Object.keys(groupedPropertyList).map((item, index) => (
					<React.Fragment key={index}>
						<Stack>
            <GroupedListTitle
								title={t(
									Resources.Portfolio.List.PortfolioSubType[
										item as PortfolioSubType
									],
								)}
							/>

							<Stack gap={1.6} mt={1.2}>
								{groupedPropertyList[item].map((r, i) => (
									<Stack key={r.id} gap={1.6} pb={1.6}>
										<PropertyListItemTitle
											data={r}
											onClick={() =>
												navigateToPortfolioDetail(
													r.id,
													PortfolioTypeEnum.Property,
												)
											}
										/>
										{i < groupedPropertyList[item].length - 1 && (
											<Divider
												sx={{
													borderWidth: 0.75,
													borderColor: theme.palette.grey[100],
												}}
											/>
										)}
                    </Stack>
								))}
							</Stack>
						</Stack>
						<Divider
							sx={{
								borderWidth: 1.5,
								borderColor: theme.palette.grey[100],
                }}
						/>
					</React.Fragment>
				))
        ) : (
				<Stack gap={1.6} mt={1.2}>
					{sortedPropertyData?.map((r) => (
						<Stack key={r.id} gap={1.6}>
            <PropertyListItemTitle
								data={r}
								onClick={() =>
                navigateToPortfolioDetail(r.id, PortfolioTypeEnum.Property)
								}
                />
                <Divider
								sx={{
									borderWidth: 0.75,
									borderColor: theme.palette.grey[100],
                  }}
                  />
                  </Stack>
					))}
          </Stack>
			)} */}

					{activeSort === SortTypes.default ? (
						(sortedPropertyData ?? []).length > 0 ? (
							<Stack>
								<GroupedListLabel
									title={t(Resources.Portfolio.List.Property)}
									setStickyTitle={setStickyTitle}
									hideGroupedListLabel={
										Object.keys(
											groupedPortfolioListWithoutServicesAndUncategorized,
										).length === 0
									}
								/>

								<Stack gap={1.6} mt={1.2}>
									{sortedPropertyData?.map((r, index) => (
										<Stack key={r.id} gap={1.6}>
											<PropertyListItemTitle
												data={r}
												onClick={() =>
													navigateToPortfolioDetail(
														r.id,
														PortfolioTypeEnum.Property,
													)
												}
											/>
											<Stack
												px={index === sortedPropertyData.length - 1 ? 0 : 1.6}
											>
												<Divider
													sx={{
														borderWidth: 0.75,
														borderColor: theme.palette.grey[100],
													}}
												/>
											</Stack>
										</Stack>
									))}
								</Stack>
							</Stack>
						) : null
					) : (sortedPropertyData ?? []).length > 0 ? (
						<Stack gap={1.6} mt={1.2}>
							{sortedPropertyData?.map((r) => (
								<Stack key={r.id} gap={1.6}>
									<PropertyListItemTitle
										data={r}
										onClick={() =>
											navigateToPortfolioDetail(
												r.id,
												PortfolioTypeEnum.Property,
											)
										}
									/>
									<Divider
										sx={{
											borderWidth: 0.75,
											borderColor: theme.palette.grey[100],
										}}
									/>
								</Stack>
							))}
						</Stack>
					) : null}

					{activeSort === SortTypes.default
						? Object.keys(groupedPortfolioListServicesAndUncategorized).map(
								(item) => (
									<GroupedPortfolioListItem
										key={item}
										item={item}
										list={groupedPortfolioListServicesAndUncategorized}
										navigateToPortfolioDetail={navigateToPortfolioDetail}
										type={PortfolioTypeEnum.Portfolio}
										setStickyTitle={setStickyTitle}
										summaryData={portfolioReportingSummary?.data}
									/>
								),
							)
						: null}
				</>
			)}
		</Stack>
	);
};
